import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { ErrorBoundary } from 'react-error-boundary';
import { format, addDays, addMonths, addYears, subHours } from 'date-fns';
import { IconButton } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { PageviewRounded } from '@mui/icons-material';
import Header from '../../components/Header';
import Nav from '../../components/Nav';
import { Body, PageContainer } from '../../components/Layout';
import { ErrorBoundaryFallback, ErrorBoundaryHandler } from '../../components/ErrorBoundary';
import { Table, Td, Th, Tr } from '../../components/Table';
import useSortableData from '../../modules/useSortableData';
import getAgentPerformances from '../../api/getAgentPerformances';
import ChatPerformanceDetailPopup from '../../components/AgentPerformances/ChatPerformanceDetailPopup';
import { TimePeriodOptions } from '../../constants/timePeriodsFilterOptions';
import { Icons } from '../../components/SurveysList/styles';
import {
  AgentPerformancesContainer,
  Container,
  Actions,
  DateContainer,
  SearchContainer,
  StyledFormControl,
  TableContainer,
} from './styles';

const AgentPerformances = () => {
  const [performances, setPerformances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(TimePeriodOptions.LAST_7_DAYS);
  const [showAgentDetailPopup, setShowAgentDetailPopup] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [searchText, setSearchText] = useState('');
  
  const getDateRange = (period) => {
    const endDate = new Date();
    let startDate;
    
    switch (period) {
      case TimePeriodOptions.LAST_24_HOURS:
        startDate = subHours(endDate, 24);
        break;
      case TimePeriodOptions.LAST_7_DAYS:
        startDate = addDays(endDate, -7);
        break;
      case TimePeriodOptions.LAST_30_DAYS:
        startDate = addDays(endDate, -30);
        break;
      case TimePeriodOptions.LAST_3_MONTHS:
        startDate = addMonths(endDate, -3);
        break;
      case TimePeriodOptions.LAST_6_MONTHS:
        startDate = addMonths(endDate, -6);
        break;
      case TimePeriodOptions.LAST_1_YEAR:
        startDate = addYears(endDate, -1);
        break;
      default:
        startDate = addDays(endDate, -7);
    }
    
    return { startDate, endDate };
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
    fetchPerformances(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const fetchPerformances = (period = selectedPeriod) => {
    setLoading(true);
    setError(null);

    const { startDate, endDate } = getDateRange(period);
    
    const params = {
      startDate: `${format(startDate, 'yyyy-MM-dd')} 00:00:00`,
      endDate: `${format(endDate, 'yyyy-MM-dd')} 23:59:59`,
    };

    getAgentPerformances(params, (response) => {
      if (response && response.data) {
        setPerformances(response.data);
      } else {
        setError('Failed to fetch agent performances');
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchPerformances();
  }, [selectedPeriod]);

  const { items: sortedItems, requestSort, sortConfig } = useSortableData(performances || []);
  
  const filteredItems = searchText.length >= 2
    ? sortedItems.filter(item => 
        item.name.toLowerCase().includes(searchText.toLowerCase())
      )
    : sortedItems;
  
  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  const handleViewAgentDetails = (userId) => {
    setSelectedAgentId(userId);
    setShowAgentDetailPopup(true);
  };

  const selectedAgentData = performances.find(agent => agent.userId === selectedAgentId);
  
  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onError={ErrorBoundaryHandler}>
        <Header />
      </ErrorBoundary>
      <PageContainer>
        <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onError={ErrorBoundaryHandler}>
          <Nav currentStep="agent-performances" />
        </ErrorBoundary>
        <Body>
          <AgentPerformancesContainer>
            <Container>
              <Actions>
                <div>
                  <DateContainer>
                    <SearchContainer>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="search by agent name"
                        value={searchText}
                        onChange={handleSearchChange}
                        size="medium"
                      />
                    </SearchContainer>
                    <StyledFormControl variant="outlined">
                      <Select
                        value={selectedPeriod}
                        onChange={handlePeriodChange}
                        displayEmpty
                      >
                        <MenuItem value={TimePeriodOptions.LAST_24_HOURS}>
                          <FormattedMessage id="agentPerformances.period.last24Hours" defaultMessage="Last 24 Hours" />
                        </MenuItem>
                        <MenuItem value={TimePeriodOptions.LAST_7_DAYS}>
                          <FormattedMessage id="agentPerformances.period.last7Days" defaultMessage="Last 7 Days" />
                        </MenuItem>
                        <MenuItem value={TimePeriodOptions.LAST_30_DAYS}>
                          <FormattedMessage id="agentPerformances.period.last30Days" defaultMessage="Last 30 Days" />
                        </MenuItem>
                        <MenuItem value={TimePeriodOptions.LAST_3_MONTHS}>
                          <FormattedMessage id="agentPerformances.period.last3Months" defaultMessage="Last 3 Months" />
                        </MenuItem>
                        <MenuItem value={TimePeriodOptions.LAST_6_MONTHS}>
                          <FormattedMessage id="agentPerformances.period.last6Months" defaultMessage="Last 6 Months" />
                        </MenuItem>
                        <MenuItem value={TimePeriodOptions.LAST_1_YEAR}>
                          <FormattedMessage id="agentPerformances.period.last1Year" defaultMessage="Last 1 Year" />
                        </MenuItem>
                      </Select>
                    </StyledFormControl>
                  </DateContainer>
                </div>
              </Actions>
              <TableContainer>
                <Table>
                  <thead>
                    <Tr>
                      <Th>
                        <button
                          type="button"
                          onClick={() => requestSort('name')}
                          className={getClassNamesFor('name')}
                        >
                          <FormattedMessage
                            id="agentPerformances.table.name"
                            defaultMessage="Name"
                          />
                        </button>
                      </Th>
                      <Th>
                        <button
                          type="button"
                          onClick={() => requestSort('email')}
                          className={getClassNamesFor('email')}
                        >
                          <FormattedMessage
                            id="agentPerformances.table.email"
                            defaultMessage="Email"
                          />
                        </button>
                      </Th>
                      <Th>
                        <button
                          type="button"
                          onClick={() => requestSort('avgCRT')}
                          className={getClassNamesFor('avgCRT')}
                        >
                          <FormattedMessage
                            id="agentPerformances.table.avgCRT"
                            defaultMessage="Avg Response Time"
                          />
                        </button>
                      </Th>
                      <Th>
                        <button
                          type="button"
                          onClick={() => requestSort('medianCRT')}
                          className={getClassNamesFor('medianCRT')}
                        >
                          <FormattedMessage
                            id="agentPerformances.table.medianCRT"
                            defaultMessage="Median Response Time"
                          />
                        </button>
                      </Th>
                      <Th>
                        <button
                          type="button"
                          onClick={() => requestSort('tenMinutesRR')}
                          className={getClassNamesFor('tenMinutesRR')}
                        >
                          <FormattedMessage
                            id="agentPerformances.table.tenMinutesRR"
                            defaultMessage="10 mins Response Rate"
                          />
                        </button>
                      </Th>
                      <Th>
                        <button
                          type="button"
                          onClick={() => requestSort('firstTenRR')}
                          className={getClassNamesFor('firstTenRR')}
                        >
                          <FormattedMessage
                            id="agentPerformances.table.firstTenRR"
                            defaultMessage="First 10 Mins Response Rate"
                          />
                        </button>
                      </Th>
                      <Th>
                        <FormattedMessage
                            id="userManagement.usersList.table.actions"
                            defaultMessage="Actions"
                        />
                      </Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <Tr>
                        <Td colSpan={7} style={{ textAlign: 'center' }}>
                          Loading...
                        </Td>
                      </Tr>
                    ) : error ? (
                      <Tr>
                        <Td colSpan={7} style={{ textAlign: 'center' }}>
                          {error}
                        </Td>
                      </Tr>
                    ) : filteredItems.length === 0 ? (
                      <Tr>
                        <Td colSpan={7} style={{ textAlign: 'center' }}>
                          No data available
                        </Td>
                      </Tr>
                    ) : (
                      filteredItems.map((row) => (
                        <Tr key={row?.userId}>
                          <Td>{row?.name}</Td>
                          <Td>{row?.email}</Td>
                          <Td style={{ textAlign: 'center'}}>{ row?.avgCRTFormatted }</Td>
                          <Td style={{ textAlign: 'center'}}>{ row?.medianCRTFormatted }</Td>
                          <Td style={{ textAlign: 'center'}}>{ row?.tenMinutesRR }</Td>
                          <Td style={{ textAlign: 'center'}}>{ row?.firstTenRR }</Td>
                          <Td style={{ textAlign: 'center'}}>
                            <Icons>
                                <IconButton
                                    aria-label="view"
                                    onClick={() => handleViewAgentDetails(row?.userId)}
                                >
                                    <PageviewRounded fontSize='large' />
                                </IconButton>
                            </Icons>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableContainer>
            </Container>
          </AgentPerformancesContainer>
          {/* Agent Detail Popup */}
          {showAgentDetailPopup && selectedAgentData && (
            <ChatPerformanceDetailPopup
              agentPerformanceData={selectedAgentData}
              onClose={() => setShowAgentDetailPopup(false)}
              selectedPeriod={selectedPeriod}
              getDateRange={getDateRange}
            />
          )}
        </Body>
      </PageContainer>
    </>
  );
};

export default AgentPerformances;
